// BUILD-046 increment 1b-ii: the searchable "Assigned to" choice of the toolbar's Filter popover (Team / Company and Assigned by Me).
//
// The popover shows: the name of the control, a search box (role=combobox), a short hint, "Selected: ..." with a Clear button, the sentence that says which people the options come from (the loaded
// rows only) and the match rule. Matches are listed ONLY after something is typed (at least one character), at most eight, each as a decorative initials circle with the displayed name and,
// where it is needed, a quiet second line. Arrow keys move through the matches, Enter or a click chooses the highlighted one, Escape closes the list (a second Escape closes the popover),
// and Tab only leaves the field: it NEVER chooses anyone, because this is a filter and not an assignment field. Choosing closes the list and the chip appears in the toolbar row.
// All rules (the options, the search, the wording, the filter a choice stands for) are in assigneeFilterModel.ts; the key rules are peopleComboboxModel.decideKey; this file draws them.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  ANY_ASSIGNEE,
  ASSIGNEE_FILTER_LABEL,
  assigneeFilterActive,
  assigneeFilterFromChoice,
  assigneeFilterSummary,
  assigneeOptionId,
  decideAssigneeKey,
  describeAssigneePicker,
  searchAssigneeOptions,
} from './assigneeFilterModel';
import type { AssigneeChoice, AssigneeFilter } from './assigneeFilterModel';
import { closeList, initialCombo, typeQuery } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import { AvatarCircle } from './PersonAvatar';
import { avatarLabels } from './personAvatarModel';

export interface AssigneePickerControl {
  /** The id prefix, unique per view (the search box is `${idPrefix}-assignee`). */
  idPrefix: string;
  /** The filter now: `any`, or one person (the stable employee id is the value). */
  filter: AssigneeFilter;
  /** The people on the rows the view has loaded (assigneeFilterModel.assigneeChoicesOf). */
  options: readonly AssigneeChoice[];
  /** Where the people come from (assigneeFilterModel.assigneeFilterScopeText). */
  scopeText: string;
  onChange: (next: AssigneeFilter) => void;
}

export function AssigneeFilterPicker({ idPrefix, filter, options, scopeText, onChange }: AssigneePickerControl) {
  const [combo, setCombo] = useState<ComboState>(initialCombo());
  // What was just chosen, said once in the polite line when the list is closed ("Filtering by Eddie.").
  const [chosenNote, setChosenNote] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputId = `${idPrefix}-assignee`;
  const labelId = `${inputId}-label`;
  const listboxId = `${inputId}-listbox`;
  const moreId = `${inputId}-more`;
  const scopeId = `${inputId}-scope`;

  const outcome = searchAssigneeOptions(options, combo.query);
  const view = describeAssigneePicker(outcome);
  const matches = outcome.matches;
  const showList = combo.open && outcome.status !== 'idle';
  const active = showList && combo.active >= 0 && combo.active < matches.length ? matches[combo.active] : null;
  const describedBy = [scopeText !== '' ? scopeId : undefined, showList && view.moreNote !== null ? moreId : undefined].filter(Boolean).join(' ');
  // The circles are labelled over the WHOLE set of options (not the matches), so a person's circle does not change as the person types.
  const badges = avatarLabels(options.map((option) => ({ id: option.employeeId, name: option.name, department: option.department })));
  const badgeOf = (employeeId: string) => badges[options.findIndex((option) => option.employeeId === employeeId)];
  // The highlighted match is brought into view (the list, and the page behind it, scroll only as far as needed).
  const activeId = active !== null ? active.employeeId : null;
  useEffect(() => {
    if (activeId === null) return;
    document.getElementById(assigneeOptionId(inputId, activeId))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId, inputId]);

  function choose(employeeId: string): void {
    const next = assigneeFilterFromChoice(matches.find((match) => match.employeeId === employeeId));
    if (next === null) return;
    onChange(next);
    // A displayed name can end with a period ("Dana D."), so the sentence's own period is added only when the name does not already end with one.
    const who = assigneeFilterSummary(next);
    setChosenNote(`Filtering by ${who}${who.endsWith('.') ? '' : '.'}`);
    setCombo(initialCombo());
  }

  function clear(): void {
    onChange(ANY_ASSIGNEE);
    setChosenNote('Showing all people.');
    // The Clear button leaves the page together with the filter, so focus moves to this pickers search box: a keyboard user stays inside the Filter popover and Escape still closes it.
    document.getElementById(inputId)?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // With no list on screen (nothing typed) Escape is the popover's own: it closes the popover.
    if (event.key === 'Escape' && !showList) {
      if (combo.open) setCombo((state) => closeList(state));
      return;
    }
    const decision = decideAssigneeKey(event.key, combo, matches);
    if (decision.handled) event.preventDefault();
    // The Escape that closes the list is not also the popover's Escape.
    if (decision.stop) event.stopPropagation();
    if (decision.choose !== null) {
      choose(decision.choose);
      return;
    }
    if (decision.state !== combo) setCombo(decision.state);
  }

  return (
    <div ref={wrapRef} data-testid="assignee-filter">
      <p id={labelId} className="mb-1 text-xs font-semibold text-gray-700">
        {ASSIGNEE_FILTER_LABEL}
      </p>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-labelledby={labelId}
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={active !== null ? assigneeOptionId(inputId, active.employeeId) : undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          value={combo.query}
          placeholder="Search by name"
          onChange={(event) => {
            setChosenNote('');
            setCombo((state) => typeQuery(state, event.target.value));
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            // Moving into the list keeps it; leaving the picker closes it. The typed text stays.
            const next = event.relatedTarget;
            if (!(next instanceof Node && wrapRef.current !== null && wrapRef.current.contains(next))) setCombo((state) => closeList(state));
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
        />
        {/* The listing is part of the panel (not a floating layer), so the popover never clips it. */}
        {showList && (
          <div className="mt-1 rounded-md border border-gray-200 bg-white" onMouseDown={(event) => event.preventDefault()}>
            <ul id={listboxId} role="listbox" aria-label="People" className="max-h-72 overflow-y-auto py-1" data-testid={`${inputId}-listbox`}>
              {matches.map((person, index) => {
                const current = filter.kind === 'person' && filter.employeeId === person.employeeId;
                const badge = badgeOf(person.employeeId);
                return (
                  <li
                    key={person.employeeId}
                    id={assigneeOptionId(inputId, person.employeeId)}
                    role="option"
                    aria-selected={current}
                    aria-label={person.accessibleName}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(person.employeeId)}
                    onMouseMove={() => setCombo((state) => (state.active === index ? state : { ...state, active: index }))}
                    className={`flex cursor-pointer select-none items-center gap-2 px-3 py-1.5 text-sm text-gray-900 ${index === combo.active ? 'bg-sky-100' : current ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-gray-50'}`}
                  >
                    {/* A decorative circle: the name is written beside it. */}
                    {badge !== undefined && <AvatarCircle badge={badge} size="compact" />}
                    <span className="min-w-0">
                      <span className="block break-words">{person.label}</span>
                      {person.detail !== '' && <span className="block break-words text-xs text-gray-500">{person.detail}</span>}
                    </span>
                  </li>
                );
              })}
              {matches.length === 0 && (
                <li role="presentation" className="px-3 py-2 text-sm text-gray-600">
                  {view.emptyText}
                </li>
              )}
            </ul>
            {view.moreNote !== null && (
              <p id={moreId} className="border-t border-gray-100 px-3 py-1 text-xs text-gray-700" data-testid={`${inputId}-more`}>
                {view.moreNote}
              </p>
            )}
          </div>
        )}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {showList ? view.announcement : chosenNote}
      </div>
      {assigneeFilterActive(filter) && (
        <p className="mt-2 text-xs">
          <button
            type="button"
            onClick={clear}
            aria-label={`Clear ${ASSIGNEE_FILTER_LABEL} filter`}
            className="rounded px-1 text-[#1f6a99] underline hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
          >
            Clear
          </button>
        </p>
      )}
      {scopeText !== '' && (
        <p id={scopeId} className="mt-2 text-xs text-gray-600" data-testid={`${inputId}-scope`}>
          {scopeText}
        </p>
      )}
    </div>
  );
}
